import React, { useState, useEffect } from 'react';

/* ---------------------------------------------------------------------------
   /cargo-counting-use-case — Visotonics loading/unloading counting page.
   Rebuilt fresh (2nd pass) directly from the verified current standalone
   build (index.html style.css?v=43 + app.js), not patched from an earlier
   draft. Every section below was checked line-by-line against that file's
   actual text, structure, and section order before being translated into
   this repo's React + Tailwind convention (see ContactPage.jsx).

   - Header/Footer are NOT rendered here — App.js renders them globally.
   - Demo form still submits to the Google Sheet endpoint (via Apps
     Script), not the /contact API — left as-is per explicit instruction.
   - Brochure button is a plain direct download — left as-is per explicit
     instruction (the rest of the site gates this behind a lead form; not
     matched here yet).
   - Confirmed section order from the real file: hero → cargo-strip →
     DEPLOYED AT → problem → workflow → fit → compare (Why VisoPercept) →
     case-study → testimonial (2 boxes) → SUPPORTED BY + PARTNERS → demo.
   - Real images for all 12 client/backer logos live in
     public/cargo-counting/logos/ (already placed).
--------------------------------------------------------------------------- */

const GOOGLE_SHEET_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbzoWt_QdkxOPCx8FLynoxmgim97Y7flNBMkEvnoY-H2JW7ohxzsavsxLD366BrvCscqzw/exec';

const ACCENT = '#25D1F4';

function DeployedAtLogos() {
  const clients = [
    { name: 'Adani', img: '/cargo-counting/logos/adani.png' },
    { name: 'Signo', img: '/cargo-counting/logos/signo.png', badge: true },
    { name: 'DP World', img: '/cargo-counting/logos/dp-world.png', badge: true },
    { name: 'Saurashtra', img: '/cargo-counting/logos/saurashtra.png' },
    { name: 'Jindal Steel', img: '/cargo-counting/logos/jindal-steel.png', badge: true },
    { name: 'Proec', img: '/cargo-counting/logos/proec.png' },
    { name: 'many more...', text: true },
  ];
  return (
    <div className="bg-white rounded-lg px-6 py-4 flex flex-nowrap items-center justify-between gap-5 w-full overflow-x-auto">
      {clients.map((c) =>
        c.text ? (
          <span key={c.name} className="text-xl font-semibold text-slate-600 whitespace-nowrap">
            {c.name}
          </span>
        ) : (
          <img
            key={c.name}
            src={c.img}
            alt={c.name}
            title={c.name}
            className={`w-auto object-contain max-w-[200px] ${
              c.badge ? 'h-[84px]' : 'h-16'
            }`}
          />
        )
      )}
    </div>
  );
}

function SupportedByLogos() {
  const backers = [
    { name: 'MeitY', img: '/cargo-counting/logos/meity.png', badge: true },
    { name: 'Department of Science & Technology', img: '/cargo-counting/logos/dst.jpg' },
    { name: 'IIT Kharagpur', img: '/cargo-counting/logos/iit-kharagpur.png', badge: true },
    { name: 'NASSCOM', img: '/cargo-counting/logos/nasscom.webp' },
    { name: 'IIT Kanpur', img: '/cargo-counting/logos/iit-kanpur.png', badge: true },
  ];
  return (
    <div className="bg-white rounded-lg px-6 py-4 flex flex-nowrap items-center justify-between gap-5 w-full overflow-x-auto">
      {backers.map((b) => (
        <img
          key={b.name}
          src={b.img}
          alt={b.name}
          title={b.name}
          className={`w-auto object-contain max-w-[200px] ${
            b.badge ? 'h-[84px]' : 'h-16'
          }`}
        />
      ))}
    </div>
  );
}

function PartnersRow() {
  const row1 = [
    { name: 'Comnet InfoSolutions', img: '/cargo-counting/logos/comnet-infosolutions.jpg' },
    { name: 'CMS Computer', img: '/cargo-counting/logos/cms-computers.jpg' },
    { name: 'SIS Infotech', img: '/cargo-counting/logos/sis-infotech.webp', extraClass: 'h-[76px]' },
    { name: 'Network TechLab', img: '/cargo-counting/logos/network-techlab.png' },
    { name: 'Netweb Technologies', img: '/cargo-counting/logos/netweb-technologies.png' },
  ];
  const row2 = [
    { name: 'Wind River', img: '/cargo-counting/logos/wind-river.png' },
    { name: 'CoSol', img: '/cargo-counting/logos/cosol.png', extraClass: 'rounded-xl overflow-hidden' },
    { name: 'Aurion Pro', img: '/cargo-counting/logos/aurionpro.jpg' },
    { name: 'Allied Vision', img: '/cargo-counting/logos/allied-vision.png' },
    { name: 'Yotta', img: '/cargo-counting/logos/yotta.png' },
  ];
  const logoClass = 'h-16 w-auto object-contain max-w-[160px]';
  const rowClass = 'bg-white rounded-lg px-6 py-4 flex flex-nowrap items-center justify-between gap-5 w-full overflow-x-auto';
  return (
    <>
      <div className={rowClass}>
        {row1.map((p) => (
          <img key={p.name} src={p.img} alt={p.name} title={p.name} className={`${logoClass} ${p.extraClass || ''}`} />
        ))}
      </div>
      <div className={`${rowClass} mt-8`}>
        {row2.map((p) => (
          <img key={p.name} src={p.img} alt={p.name} title={p.name} className={`${logoClass} ${p.extraClass || ''}`} />
        ))}
      </div>
    </>
  );
}

export default function CargoCountingPage() {
  // Microsoft Clarity — scoped to only this page, per explicit instruction
  // (not site-wide). Injected on mount, removed on unmount so it doesn't
  // keep tracking once the visitor navigates to a different page in this
  // single-page app. Same project (yh3rajgo5n) used on the earlier
  // standalone build of this page.
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "yh3rajgo5n");
    `;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      // Note: this does not stop Clarity's own tag script (loaded async
      // above) from continuing to run if it already started — it only
      // removes our injected <script> tag itself. For a single-page app
      // where visitors may navigate away without a full reload, this is
      // an acceptable, honest limitation rather than a false guarantee.
    };
  }, []);

  // Google Analytics 4 (gtag.js) — scoped to only this page, same rationale
  // as Clarity above: not site-wide, per explicit instruction. Two script
  // tags are needed (the external gtag.js loader, plus our inline config
  // call) since gtag's snippet is normally split that way; both are
  // injected on mount and removed on unmount.
  useEffect(() => {
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-QMNSKJ4FW2';
    document.head.appendChild(gtagScript);

    const configScript = document.createElement('script');
    configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-QMNSKJ4FW2');
    `;
    document.head.appendChild(configScript);

    return () => {
      document.head.removeChild(gtagScript);
      document.head.removeChild(configScript);
      // Same honest caveat as Clarity: removing these tags does not
      // retroactively stop gtag.js if it already started running before
      // the visitor navigated away within this single-page app.
    };
  }, []);

  // LinkedIn Insight Tag — scoped to only this page, same rationale as
  // Clarity/GA4 above: not site-wide, per the same explicit instruction.
  // Partner ID 9732122, from LinkedIn Campaign Manager. The noscript/<img>
  // fallback pixel LinkedIn's snippet normally includes is intentionally
  // left out: it only matters for visitors with JavaScript disabled, and
  // this page cannot render at all without JavaScript (it's a React SPA),
  // so that fallback would be dead code here — same honest-omission
  // approach already used for the Clarity/GA4 unmount caveats.
  useEffect(() => {
    const configScript = document.createElement('script');
    configScript.type = 'text/javascript';
    configScript.innerHTML = `
      _linkedin_partner_id = "9732122";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `;
    document.head.appendChild(configScript);

    const loaderScript = document.createElement('script');
    loaderScript.type = 'text/javascript';
    loaderScript.innerHTML = `
      (function(l) {
      if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[]}
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);})(window.lintrk);
    `;
    document.head.appendChild(loaderScript);

    return () => {
      document.head.removeChild(configScript);
      document.head.removeChild(loaderScript);
      // Same honest caveat as Clarity/GA4: removing these tags does not
      // retroactively stop insight.min.js if it already started running
      // before the visitor navigated away within this single-page app.
    };
  }, []);

  // Meta Pixel — scoped to only this page, same rationale as the other
  // three tracking scripts above. Pixel ID 1134188736240804, from Meta
  // Events Manager. The noscript/<img> fallback Meta's snippet normally
  // includes is left out for the same reason as LinkedIn's above: it only
  // matters for visitors with JavaScript disabled, and this page cannot
  // render at all without JavaScript, so that fallback is dead code here.
  useEffect(() => {
    const pixelScript = document.createElement('script');
    pixelScript.type = 'text/javascript';
    pixelScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1134188736240804');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(pixelScript);

    return () => {
      document.head.removeChild(pixelScript);
      // Same honest caveat as the other three: removing this tag does not
      // retroactively stop fbevents.js if it already started running
      // before the visitor navigated away within this single-page app.
    };
  }, []);

  // Fires a named event into GA4's dataLayer. Used to mark specific
  // points in the funnel (form started, enquiry received) rather than
  // only tracking the final successful submission, per Pranav's
  // measurement requirements. Guards against gtag not being ready yet
  // (e.g. if this fires before the script above finishes loading).
  const trackEvent = (eventName, params = {}) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  };

  // Fires the LinkedIn Insight Tag's "Demo Request Submitted" conversion
  // (conversion_id 28854804, event-specific tracking — this page's
  // demo-request submission doesn't navigate to a new URL, so LinkedIn's
  // page-load-based tracking can't be used). Intentionally called only
  // from the demo-request submit flow, not the brochure-download one:
  // this conversion is named and categorized (Lead) specifically for the
  // higher-intent demo request, and mixing in the brochure download would
  // blur what it actually measures. Guards against lintrk not being ready
  // yet, same rationale as trackEvent above.
  const trackLinkedInConversion = () => {
    if (typeof window.lintrk === 'function') {
      window.lintrk('track', { conversion_id: 28854804 });
    }
  };

  const trackMetaConversion = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'CompleteRegistration');
    }
  };

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [demoFormStarted, setDemoFormStarted] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [brochureForm, setBrochureForm] = useState({ name: '', email: '', phone: '' });
  const [brochureFormStarted, setBrochureFormStarted] = useState(false);
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);
  const [brochureStatus, setBrochureStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  // Updates a demo-form field, firing "form_started" once on the first
  // keystroke in any field — not on every change — so it marks the
  // moment someone begins filling the form, distinct from a full
  // submission.
  const updateFormField = (field, value) => {
    if (!demoFormStarted) {
      trackEvent('form_started', { form_name: 'demo_request' });
      setDemoFormStarted(true);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateBrochureFormField = (field, value) => {
    if (!brochureFormStarted) {
      trackEvent('form_started', { form_name: 'brochure_request' });
      setBrochureFormStarted(true);
    }
    setBrochureForm((prev) => ({ ...prev, [field]: value }));
  };

  // Common personal/free email providers — rejected so only work emails
  // are accepted. This is a blocklist, not a perfect detector: it catches
  // the overwhelming majority of personal addresses, but a determined
  // person could still use an uncommon free provider not on this list.
  const PERSONAL_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'yahoo.co.in', 'hotmail.com', 'outlook.com',
    'live.com', 'aol.com', 'icloud.com', 'protonmail.com', 'rediffmail.com',
    'zoho.com', 'mail.com', 'gmx.com', 'yandex.com',
  ];

  const isWorkEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase().trim();
    return domain && !PERSONAL_EMAIL_DOMAINS.includes(domain);
  };

  // Accepts Indian mobile numbers with or without a +91 prefix, and
  // generic 10-15 digit numbers for other countries — intentionally
  // permissive rather than strict, since rejecting a real customer's
  // valid number is worse than letting through an edge case.
  const isValidPhone = (phone) => {
    const digitsOnly = phone.replace(/[\s\-()]/g, '');
    return /^(\+?\d{10,15})$/.test(digitsOnly);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone) {
      setStatus('Please fill in your name, work email, and phone number.');
      return;
    }
    if (!isWorkEmail(form.email)) {
      setStatus('Please use your work email address, not a personal one (e.g. Gmail, Yahoo).');
      return;
    }
    if (!isValidPhone(form.phone)) {
      setStatus('Please enter a valid phone number.');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: form.message,
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
    };

    setSubmitting(true);
    setStatus('Sending...');
    try {
      await fetch(GOOGLE_SHEET_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      });
      setStatus("Thanks — your request has been received. Someone from the Visotonics team will reach out within one business day.");
      trackEvent('enquiry_received', { form_name: 'demo_request' });
      trackLinkedInConversion();
      trackMetaConversion();
      setForm({ name: '', email: '', phone: '', message: '' });
      setDemoFormStarted(false);
    } catch (err) {
      console.error('Form submission failed:', err);
      setStatus('Something went wrong sending your request. Please try again, or email contact@visotonics.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submits the brochure-request details to the same Sheet endpoint (as a
  // separate row, tagged so it's distinguishable from demo requests), then
  // triggers the actual file download only after a successful submission —
  // matching the "submit details then get details" gating already used
  // elsewhere on the site for deck/brochure downloads.
  const submitBrochureRequest = async (e) => {
    e.preventDefault();

    if (!brochureForm.name || !brochureForm.email || !brochureForm.phone) {
      setBrochureStatus('Please fill in your name, work email, and phone number.');
      return;
    }
    if (!isWorkEmail(brochureForm.email)) {
      setBrochureStatus('Please use your work email address, not a personal one (e.g. Gmail, Yahoo).');
      return;
    }
    if (!isValidPhone(brochureForm.phone)) {
      setBrochureStatus('Please enter a valid phone number.');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const payload = {
      name: brochureForm.name,
      email: brochureForm.email,
      phone: brochureForm.phone,
      message: 'Brochure download request',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
    };

    setBrochureSubmitting(true);
    setBrochureStatus('Sending...');
    try {
      await fetch(GOOGLE_SHEET_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      });

      // Trigger the actual file download only now, after a successful submit.
      const link = document.createElement('a');
      link.href = '/cargo-counting/Brochure.pdf';
      link.download = 'Visotonics-Brochure.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setBrochureStatus('Thanks — your download should begin shortly.');
      trackEvent('enquiry_received', { form_name: 'brochure_request' });
      trackMetaConversion();
      setBrochureForm({ name: '', email: '', phone: '' });
      setBrochureFormStarted(false);
      setTimeout(() => setBrochureOpen(false), 1500);
    } catch (err) {
      console.error('Brochure request failed:', err);
      setBrochureStatus('Something went wrong. Please try again, or email contact@visotonics.com directly.');
    } finally {
      setBrochureSubmitting(false);
    }
  };

  return (
    <div className="bg-[#070A13] text-[#E4EBF1]">
      {/* HERO */}
      <div className="bg-[#090E1A] py-4">
        <section className="grid grid-cols-1 md:grid-cols-[0.75fr_1.25fr] items-stretch min-h-[460px]">
          <div className="flex flex-col justify-between px-6 md:pl-[10%] md:pr-[4%] py-0">
            <h1
            className="font-bold leading-[1.02] tracking-tight mb-4"
            style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(36px,4.8vw,61px)' }}
          >
            What actually
            <br />
            left your
            <br />
            <em className="not-italic" style={{ color: ACCENT }}>loading bay?</em>
          </h1>
          <div>
            <p className="text-[22px] leading-snug max-w-[490px] mb-4">
              Turn cargo movement into a count you can review—and a visual record you can go back to.
            </p>
            <p className="text-sm leading-relaxed text-[#98A4B3] max-w-[520px] mb-6">
              Visotonics brings camera-based counting to loading and unloading, helping teams reconcile
              dispatch with what happened on the ground.
            </p>
            <div className="flex flex-wrap gap-3.5 items-center justify-center">
              <a
                href="#demo"
                className="flex-1 inline-flex items-center justify-center gap-3 px-[18px] py-[15px] rounded-md font-extrabold text-[15px] text-center"
                style={{ background: ACCENT, color: '#04141A' }}
              >
                Book A Demo <span>↗</span>
              </a>
              <button
                type="button"
                onClick={() => setBrochureOpen(true)}
                className="flex-1 inline-flex items-center justify-center gap-3 px-[18px] py-[15px] rounded-md font-extrabold text-[15px] border border-white/15 text-white text-center"
              >
                Download Brochure <span>↓</span>
              </button>
            </div>
            <p className="text-[13px] text-[#98A4B3] mt-6 whitespace-nowrap">
              Patented Technology | Awarded Best Industry AI Application 2025 by CII
            </p>
            <p className="text-[13px] text-[#98A4B3] mt-1 whitespace-nowrap">
              10+ Years of Experience in Physical-AI
            </p>
          </div>
        </div>
        <div className="relative overflow-hidden bg-[#090E1A] flex items-center justify-center">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-contain"
            src="/cargo-counting/hero-loading.mp4"
          />
        </div>
      </section>
      </div>

      {/* CARGO STRIP */}
      <div className="px-6 md:px-[12%] py-6 flex items-center justify-between gap-5 border-b border-white/10 flex-wrap text-sm bg-[#070A13]">
        <span className="text-[9px] tracking-wider text-[#6B7684]">EXPLORE YOUR CARGO TYPE</span>
        {['Cartons', 'Gunny bags', 'FIBC bags', 'Pallets', 'Drums'].map((c) => (
          <b key={c} className="font-bold">{c}</b>
        ))}
      </div>

      {/* DEPLOYED AT — sits right after cargo strip, before Problem */}
      <section className="px-6 md:px-[12%] py-14 bg-[#090E1A]">
        <p className="text-xs font-extrabold tracking-[2.5px] mb-5">DEPLOYED AT</p>
        <DeployedAtLogos />
      </section>

      {/* PROBLEM */}
      <section className="px-6 md:px-[12%] py-16 bg-[#111827]">
        <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">THE MOMENT THAT MATTERS</p>
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 items-start">
          <h2 className="font-bold leading-tight tracking-tight" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
            A dispatch sheet tells<br />one side of the story.
          </h2>
          <p className="text-base leading-relaxed text-[#98A4B3]">
            Busy bays, shifting camera coverage and manual counts make discrepancies difficult to trace.
            Give operations teams a visual reference when the numbers don't match.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-10 mt-12">
          {[
            { n: '01 / COUNT', h: 'Review cargo movement', p: 'Use camera-based item counting to support loading and unloading reconciliation.' },
            { n: '02 / VERIFY', h: 'Return to the visual record', p: 'Review session evidence alongside counts when a shortage or mismatch needs investigation.' },
            { n: '03 / IMPROVE', h: 'Understand the operation', p: 'Explore session and throughput information to identify where closer attention is needed.' },
          ].map((s) => (
            <article key={s.n} className="border-t-2 pt-6" style={{ borderColor: ACCENT }}>
              <span className="text-[10px] tracking-wider text-[#6B7684]">{s.n}</span>
              <h3 className="text-xl font-semibold tracking-tight mt-2 mb-2">{s.h}</h3>
              <p className="text-sm leading-relaxed text-[#98A4B3]">{s.p}</p>
            </article>
          ))}
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="px-6 md:px-[12%] py-16 bg-[#090E1A] grid md:grid-cols-2 gap-10">
        <div>
          <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">FROM THE BAY TO THE REVIEW</p>
          <h2 className="font-bold leading-tight tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
            Visibility where<br />the work happens.
          </h2>
          <p className="text-base leading-relaxed text-[#98A4B3] max-w-[400px] mb-5">
            Portable camera placement and local processing bring the workflow closer to cargo movement.
          </p>
          <a href="#demo" className="inline-block border-b-2 pb-2 text-sm font-bold" style={{ borderColor: ACCENT }}>
            Explore your setup ↗
          </a>
        </div>
        <ol className="list-none m-0 p-0">
          {[
            { n: '01', h: 'Position the camera', p: 'Assess the loading area, cargo path, lighting and safe camera placement.' },
            { n: '02', h: 'Capture and count', p: 'Vision AI processes the configured movement flow to produce counts and visual records.' },
            { n: '03', h: 'Review the session', p: 'Use the dashboard to support reconciliation and investigate exceptions.' },
          ].map((s, i) => (
            <li key={s.n} className={`flex gap-7 border-b border-white/10 py-4 ${i === 0 ? 'pt-0' : ''}`}>
              <span className="text-3xl text-[#6B7684]" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif" }}>{s.n}</span>
              <div>
                <h3 className="text-xl font-semibold tracking-tight mb-1.5">{s.h}</h3>
                <p className="text-sm leading-relaxed text-[#98A4B3]">{s.p}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* FIT */}
      <section id="fit" className="px-6 md:px-[12%] py-16 bg-[#111827]">
        <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">BUILT AROUND YOUR WORKFLOW</p>
        <h2 className="font-bold leading-tight tracking-tight mb-8" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
          One bay first.<br />A clear path to scale.
        </h2>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="border-l-2 pl-6" style={{ borderColor: ACCENT }}>
            <h3 className="text-xl font-semibold mb-2">A useful starting point</h3>
            <ul className="list-disc pl-4 text-sm leading-relaxed text-[#98A4B3] space-y-1">
              <li>Warehouses reconciling loaded and received quantities</li>
              <li>3PL teams managing customer dispatch queries</li>
              <li>Manufacturing sites with high-volume outward movement</li>
            </ul>
          </div>
          <div className="border-l-2 pl-6" style={{ borderColor: ACCENT }}>
            <h3 className="text-xl font-semibold mb-2">What we assess together</h3>
            <ul className="list-disc pl-4 text-sm leading-relaxed text-[#98A4B3] space-y-1">
              <li>Cartons, sacks, FIBC bags, pallets or drums</li>
              <li>Occlusion, reverse movement and loading speed</li>
              <li>Camera coverage, local processing and record retention</li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHY VISOPERCEPT */}
      <section className="px-6 md:px-[12%] py-16 bg-[#090E1A]">
        <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">WHY VISOPERCEPT</p>
        <h2 className="font-bold leading-tight tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
          Built for real-world footage,<br />not just clean images.
        </h2>
        <p className="text-[15px] leading-relaxed text-[#98A4B3] max-w-[640px] mb-9">
          Independent benchmark · Build for complex, chaotic and edge case scenarios where precision is
          non-negotiable · Processing 400K+ images daily · container ID recognition across varying image
          quality — the same conditions loading bays actually operate in.
        </p>
        <div className="grid md:grid-cols-2 border border-white/10 rounded-lg overflow-hidden">
          <div className="p-7" style={{ background: 'rgba(37,209,244,0.08)', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="text-xs font-extrabold tracking-wider mb-4" style={{ color: ACCENT }}>VISOPERCEPT</div>
            {[
              ['Excellent image quality', '98.71%'],
              ['Challenging image quality', '93.27%'],
              ['Poor image quality', '84.57%'],
              ['Multi-frame / container', '100%'],
            ].map(([label, val], i) => (
              <div key={label} className={`flex justify-between items-center py-2.5 text-sm text-[#98A4B3] ${i > 0 ? 'border-t border-white/10' : ''}`}>
                <span>{label}</span>
                <b className="font-bold text-base" style={{ color: ACCENT }}>{val}</b>
              </div>
            ))}
          </div>
          <div className="p-7 bg-[#070A13]">
            <div className="text-xs font-extrabold tracking-wider text-[#6B7684] mb-4">BEST GENERIC MODEL (GEMINI)</div>
            {[
              ['Excellent image quality', '89.79%'],
              ['Challenging image quality', '76.60%'],
              ['Poor image quality', '37.14%'],
              ['Multi-frame / container', '94%'],
            ].map(([label, val], i) => (
              <div key={label} className={`flex justify-between items-center py-2.5 text-sm text-[#98A4B3] ${i > 0 ? 'border-t border-white/10' : ''}`}>
                <span>{label}</span>
                <b className="font-bold text-base text-white">{val}</b>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] leading-relaxed text-[#98A4B3] mt-6 max-w-[680px]">
          VisoPercept, our proprietary vision AI built from the ground up, outperformed leading global
          models on targeted industrial benchmarks.
          <br />· Delivers the final 10% of accuracy that mission-critical operations demand.
        </p>
      </section>

      {/* CASE STUDY — teaser linking to the full write-up on the blog */}
      <section className="px-6 md:px-[12%] py-16 bg-[#111827]">
        <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">CASE STUDY</p>
        <h2 className="font-bold leading-tight tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
          A Verifiable Count, at Every Bay, Every Session.
        </h2>
        <p className="text-sm leading-relaxed text-[#98A4B3] max-w-[600px] mb-6">
          How a logistics operator running multiple loading bays replaced manual tallies with an
          automatic count and a visual record for every session — closing the gap between what left
          the dock and what got recorded.
        </p>
        <a
          href="https://visotonics.com/resources/blog/cargo-counting-verifiable-loading-bay"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-bold"
          style={{ color: ACCENT }}
        >
          Read the full case study ↗
        </a>
      </section>

      {/* TESTIMONIALS — four, each with its own attribution */}
      <section className="px-6 md:px-[12%] py-16 bg-[#070A13]">
        <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5">IN THEIR WORDS</p>
        <h2 className="font-bold leading-tight tracking-tight mb-3.5" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
          What operators use Visotonics for.
        </h2>
        <p className="text-sm text-[#6B7684] mb-9 max-w-[800px] whitespace-nowrap">
          A closer look at how teams use camera-based intelligence to improve visibility across their operations.
        </p>
        <div className="grid md:grid-cols-2 gap-7">
          {[
            {
              quote: "We used to rely on people on the ground to keep count during loading and unloading. With Visotonics, the camera keeps track of the cargo as it moves, so we have a count to check later if there's a mismatch.",
              name: 'Operation Head',
              role: 'Logistics Company, Rajasthan',
            },
            {
              quote: "When several trucks are being loaded at once, keeping an accurate count is hard. Visotonics gives us a running count from the cameras, which makes it much easier for the team to keep track without having someone count everything manually.",
              name: 'Dispatch Manager',
              role: 'CFS Mundra',
            },
            {
              quote: "Earlier, if a customer disputed the quantity we'd dispatched, we had no way to check except asking the loading crew what they remembered. Now we can pull up the session and see exactly what went out.",
              name: 'Warehouse Manager',
              role: 'Manufacturing Facility, Gujarat',
            },
            {
              quote: "Our cameras were already in place for security. Visotonics started using the same feeds for counting, so we didn't need any new hardware or a separate setup to get started.",
              name: 'Operations Lead',
              role: 'Container Terminal, Gandhidham',
            },
          ].map((t, i) => (
            <article key={i} className="h-full box-border p-[34px] rounded-lg border border-white/15" style={{ background: 'rgba(255,255,255,0.025)' }}>
              <div className="text-[58px] leading-[0.7] mb-[22px]" style={{ fontFamily: 'Georgia,serif', color: ACCENT }}>&ldquo;</div>
              <p className="text-[17px] leading-relaxed mb-[22px]">{t.quote}</p>
              <div className="w-[30px] h-0.5 mb-3.5" style={{ background: ACCENT }} />
              <p className="text-[13px] text-[#6B7684] leading-relaxed">
                <strong className="text-[#98A4B3]">{t.name}</strong><br />{t.role}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* SUPPORTED BY / PARTNERS */}
      <section className="px-6 md:px-[12%] py-[60px] border-t border-white/10 bg-[#090E1A]">
        <p className="text-xs font-extrabold tracking-[2.5px] mt-10 mb-5">SUPPORTED BY</p>
        <SupportedByLogos />

        <div className="border-t border-white/10 mt-10" />

        <p className="text-xs font-extrabold tracking-[2.5px] mt-10 mb-5">PARTNERS</p>
        <PartnersRow />
      </section>

      {/* DEMO REQUEST */}
      <section id="demo" className="px-6 md:px-[12%] py-16 bg-[#070A13] grid md:grid-cols-2 gap-10 items-stretch">
        <div className="text-left flex flex-col">
          <div>
            <p className="text-[10px] font-extrabold tracking-[2px] mb-3.5" style={{ color: ACCENT }}>LET'S LOOK AT YOUR LOADING BAY</p>
            <h2 className="font-bold leading-[1.15] tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk','Inter',sans-serif", fontSize: 'clamp(30px,3.3vw,44px)' }}>
              Bring a workflow.<br />Leave with a<br /><em className="not-italic" style={{ color: ACCENT }}>clear next step.</em>
            </h2>
            <p className="text-base leading-relaxed text-[#98A4B3] max-w-[500px]">
              Tell us what moves through your bay and what you need to verify. We'll discuss fit,
              demonstrate the relevant workflow and define what a pilot should measure.
            </p>
          </div>
          {/* Sized to the image's own aspect ratio (1600x752) instead of a
              fixed height, so the box exactly matches the photo — no
              letterboxed empty space, no crop, and all 4 corners fall on
              real image content so rounded-lg reads on every corner. */}
          <img
            src="/cargo-counting/demo-section-image.jpeg"
            alt="Visotonics loading bay workflow"
            className="mt-6 rounded-lg max-w-[500px] w-full block"
            style={{ aspectRatio: '1600 / 752' }}
          />
          <div className="flex items-center gap-2 text-sm flex-wrap mt-6">
            <b className="mr-1">Contact Us:</b>
            <span className="text-[#98A4B3]">contact@visotonics.com</span>
            <span className="text-[#6B7684]">|</span>
            <span className="text-[#98A4B3]">+91 9651891556</span>
          </div>
        </div>

        <form onSubmit={submit} className="bg-[#070A13] p-8 rounded-lg border border-white/10">
          <h3 className="text-2xl font-semibold mb-0">Request Your Workflow Demo</h3>

          <label className="block text-xs font-bold mt-4 mb-1.5 text-[#98A4B3]">Your name</label>
          <input
            autoComplete="name"
            maxLength={100}
            required
            className="w-full p-3 rounded-md border border-white/15 bg-[#090E1A] text-[#E4EBF1]"
            value={form.name}
            onChange={(e) => updateFormField('name', e.target.value)}
          />

          <label className="block text-xs font-bold mt-4 mb-1.5 text-[#98A4B3]">Work email</label>
          <input
            type="email"
            autoComplete="email"
            maxLength={150}
            required
            className="w-full p-3 rounded-md border border-white/15 bg-[#090E1A] text-[#E4EBF1]"
            value={form.email}
            onChange={(e) => updateFormField('email', e.target.value)}
          />

          <label className="block text-xs font-bold mt-4 mb-1.5 text-[#98A4B3]">Phone number</label>
          <input
            type="tel"
            autoComplete="tel"
            maxLength={20}
            required
            className="w-full p-3 rounded-md border border-white/15 bg-[#090E1A] text-[#E4EBF1] placeholder:text-[#5A6472]"
            value={form.phone}
            onChange={(e) => updateFormField('phone', e.target.value)}
            placeholder="+91 98765 43210"
          />

          <label className="block text-xs font-bold mt-4 mb-1.5 text-[#98A4B3]">What would you like to verify?</label>
          <textarea
            rows={3}
            maxLength={1200}
            className="w-full p-3 rounded-md border border-white/15 bg-[#090E1A] text-[#E4EBF1] resize-y placeholder:text-[#5A6472]"
            value={form.message}
            onChange={(e) => updateFormField('message', e.target.value)}
            placeholder="For example: mismatches between loaded quantities and dispatch records"
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full mt-5 py-[15px] rounded-md font-extrabold text-[13px] justify-center flex items-center"
            style={{ background: ACCENT, color: '#04141A' }}
          >
            {submitting ? 'Sending...' : 'Prepare Demo Request'}
          </button>

          <p className="text-xs text-[#98A4B3] mt-3 leading-relaxed">
            {status || "We'll get back to you within one business day."}
          </p>
        </form>
      </section>

      {/* BROCHURE REQUEST MODAL */}
      {brochureOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => setBrochureOpen(false)}
        >
          <div
            className="bg-[#090E1A] border border-white/15 rounded-lg p-8 max-w-[440px] w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setBrochureOpen(false)}
              className="absolute top-4 right-4 text-[#98A4B3] text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-2xl font-semibold mb-2">Get the Brochure</h3>
            <p className="text-sm text-[#98A4B3] mb-6">
              Share a few details and we'll start your download.
            </p>

            <form onSubmit={submitBrochureRequest}>
              <label className="block text-xs font-bold mt-3 mb-1.5 text-[#98A4B3]">Your name</label>
              <input
                autoComplete="name"
                maxLength={100}
                required
                className="w-full p-3 rounded-md border border-white/15 bg-[#070A13] text-[#E4EBF1]"
                value={brochureForm.name}
                onChange={(e) => updateBrochureFormField('name', e.target.value)}
              />

              <label className="block text-xs font-bold mt-3 mb-1.5 text-[#98A4B3]">Work email</label>
              <input
                type="email"
                autoComplete="email"
                maxLength={150}
                required
                className="w-full p-3 rounded-md border border-white/15 bg-[#070A13] text-[#E4EBF1]"
                value={brochureForm.email}
                onChange={(e) => updateBrochureFormField('email', e.target.value)}
              />

              <label className="block text-xs font-bold mt-3 mb-1.5 text-[#98A4B3]">Phone number</label>
              <input
                type="tel"
                autoComplete="tel"
                maxLength={20}
                required
                className="w-full p-3 rounded-md border border-white/15 bg-[#070A13] text-[#E4EBF1] placeholder:text-[#5A6472]"
                value={brochureForm.phone}
                onChange={(e) => updateBrochureFormField('phone', e.target.value)}
                placeholder="+91 98765 43210"
              />

              <button
                type="submit"
                disabled={brochureSubmitting}
                className="w-full mt-5 py-[15px] rounded-md font-extrabold text-[13px] justify-center flex items-center"
                style={{ background: ACCENT, color: '#04141A' }}
              >
                {brochureSubmitting ? 'Sending...' : 'Download Brochure'}
              </button>

              {brochureStatus && (
                <p className="text-xs text-[#98A4B3] mt-3 leading-relaxed">{brochureStatus}</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
